import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

/**
 * Attaches an HLS source to a `<video>` element — native HLS on Safari, `hls.js` everywhere else
 * that supports MSE, or a plain download-link fallback otherwise. Shared by `HlsPlayer` and the
 * thumbnail picker (thumbnail-picker.tsx), which needs its own independent `<video>` element
 * rather than reusing `HlsPlayer` itself (see design.md's rationale — it shouldn't fight over
 * `currentTime`/paused state with whatever `HlsPlayer` is doing elsewhere on the same page).
 */
export function useHlsSource(
  videoRef: RefObject<HTMLVideoElement>,
  src: string,
  autoPlay?: boolean,
  /** Called on a fatal load failure (native `<video>` error event, or a fatal hls.js error) — the
   * default `HlsPlayer` usage below leaves this unset and simply falls through to the browser's
   * own default behavior, but callers like the thumbnail picker that build custom UI around
   * "has this loaded yet?" need to know when the answer is "it never will," not sit disabled
   * forever with no explanation. */
  onError?: (message: string) => void,
  /** Skip the native-HLS branch even if `canPlayType` claims support, forcing `hls.js`/MediaSource
   * instead. Needed by the thumbnail picker: with `crossOrigin` set (required so canvas capture
   * isn't tainted), a native fetch of this app's HLS source sends a `Range` request header, which
   * turns it into a non-simple CORS request requiring a preflight (OPTIONS) — and the upstream
   * video CDN (owned by a separate, external VOD pipeline) only allows GET/HEAD, so the preflight
   * fails and the whole load is blocked. hls.js's own segment fetches are plain GETs with no
   * `Range` header, which stay "simple" and never trigger a preflight. `HlsPlayer` never sets
   * this — it doesn't set `crossOrigin` either, so it isn't affected by this at all. */
  forceMse?: boolean,
  /** Force the highest-resolution rendition instead of letting hls.js's adaptive bitrate logic
   * pick one. ABR starts low and only ramps up based on sustained playback bandwidth estimation —
   * since the thumbnail picker never actually plays continuously, it would otherwise stay stuck
   * on the lowest rendition forever, producing a grainy capture. Normal playback (`HlsPlayer`)
   * leaves this unset so real viewers still get proper adaptive streaming. */
  forceHighestQuality?: boolean,
): void {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoError = () => onError?.('The video failed to load.');
    video.addEventListener('error', handleVideoError);

    if (!forceMse && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari can play HLS natively.
      video.src = src;
      if (autoPlay) video.play().catch(() => undefined);
      return () => video.removeEventListener('error', handleVideoError);
    }
    if (!Hls.isSupported()) {
      // No native, no MSE — render the URL as a download fallback.
      video.src = src;
      return () => video.removeEventListener('error', handleVideoError);
    }
    const hls = new Hls();
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) onError?.(`The video failed to load (${data.details}).`);
    });
    if (forceHighestQuality) {
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        let bestIndex = 0;
        let bestPixels = -1;
        hls.levels.forEach((level, i) => {
          const pixels = (level.width ?? 0) * (level.height ?? 0);
          if (pixels > bestPixels) {
            bestPixels = pixels;
            bestIndex = i;
          }
        });
        hls.currentLevel = bestIndex;
      });
    }
    hls.loadSource(src);
    hls.attachMedia(video);
    if (autoPlay) hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => undefined));
    return () => {
      video.removeEventListener('error', handleVideoError);
      hls.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
}

export function HlsPlayer({
  src,
  poster,
  style,
  autoPlay,
  onEnded,
  playOverlay,
  captionUrl,
}: {
  src: string;
  poster?: string;
  style?: CSSProperties;
  /** Attempts playback as soon as `src` loads — used by the playlist feed to auto-advance between
   * videos. Browsers may still block this without a prior user gesture; the visible `controls`
   * remain the fallback either way. */
  autoPlay?: boolean;
  onEnded?: () => void;
  /** Shows a centered play-button overlay while paused, and darkens the frame on hover — used by
   * the embed page. Off by default so other callers (watch page, playlist feed) are unaffected. */
  playOverlay?: boolean;
  /** A ready caption track's URL, if this video has one. Rendered as a native `<track>` with no
   * `default` attribute, so every browser's own captions control appears — off by default — with
   * no custom toggle UI needed (see design.md). */
  captionUrl?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(true);

  useHlsSource(videoRef, src, autoPlay);

  const video = (
    <video
      ref={videoRef}
      controls
      poster={poster}
      onEnded={onEnded}
      onPlay={() => setPaused(false)}
      onPause={() => setPaused(true)}
      style={{ display: 'block', width: '100%', maxHeight: '70vh', ...style }}
    >
      {captionUrl && <track kind="captions" src={captionUrl} srcLang="en" label="English" />}
    </video>
  );

  if (!playOverlay) return video;

  return (
    <div className="hls-player-wrap">
      {video}
      {paused && (
        <>
          <div className="hls-player-scrim" />
          <button
            type="button"
            className="hls-player-play-overlay"
            aria-label="Play video"
            onClick={() => videoRef.current?.play()}
          >
            <span className="hls-player-play-icon" aria-hidden="true">
              ▶
            </span>
          </button>
        </>
      )}
    </div>
  );
}
